/**
 * Hook for managing plugin builder sessions list (CRUD).
 */

import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import type { PluginBuilderSession } from '../types';

interface UsePluginBuilderSessionsReturn {
  sessions: PluginBuilderSession[];
  isLoading: boolean;
  isCreating: boolean;
  error: Error | null;
  createSession: (params?: { name?: string }) => Promise<string | null>;
  deleteSession: (sessionId: string) => Promise<boolean>;
  refresh: () => Promise<void>;
}

export function usePluginBuilderSessions(): UsePluginBuilderSessionsReturn {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    data,
    isLoading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: queryKeys.pluginBuilder.sessions(),
    queryFn: async () => {
      const response = await api.listPluginBuilderSessions();
      return response.sessions as PluginBuilderSession[];
    },
  });

  const sessions = data ?? [];
  const error =
    queryError instanceof Error
      ? queryError
      : queryError
        ? new Error('Failed to fetch sessions')
        : null;

  // Create session mutation
  const createMutation = useMutation({
    mutationFn: (params?: { name?: string }) => api.createPluginBuilderSession(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.pluginBuilder.sessions() });
    },
  });

  const createSession = useCallback(
    async (params?: { name?: string }): Promise<string | null> => {
      try {
        const response = await createMutation.mutateAsync(params);
        navigate(`/a/arche_plugin_builder/${response.sessionId}`);
        return response.sessionId;
      } catch {
        return null;
      }
    },
    [createMutation, navigate],
  );

  // Delete session mutation
  const deleteMutation = useMutation({
    mutationFn: (sessionId: string) => api.deletePluginBuilderSession(sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.pluginBuilder.sessions() });
    },
  });

  const deleteSession = useCallback(
    async (sessionId: string): Promise<boolean> => {
      try {
        await deleteMutation.mutateAsync(sessionId);
        return true;
      } catch {
        return false;
      }
    },
    [deleteMutation],
  );

  const refresh = useCallback(async () => {
    await refetch();
  }, [refetch]);

  return {
    sessions,
    isLoading,
    isCreating: createMutation.isPending,
    error:
      createMutation.error instanceof Error
        ? createMutation.error
        : deleteMutation.error instanceof Error
          ? deleteMutation.error
          : error,
    createSession,
    deleteSession,
    refresh,
  };
}
